<template>
  <div class="services-review-list">
    <!-- Desktop: Table Layout -->
    <table class="services-table">
      <thead>
        <tr>
          <th>Id</th>
          <th>{{ t('Serviço') }}</th>
          <th>{{ t('Status') }}</th>
          <th>{{ t('Valor') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="service in services" :key="service.compromisso_servico_id">
          <td class="id-column">
            <a
              href="javascript:void(0)"
              class="id-link"
              :data-testid="`agenda-review-edit-servico-${service.compromisso_servico_id}`"
              @click.prevent="handleEditClick(service.compromisso_servico_id ?? 0)"
            >
              #{{ service.compromisso_servico_id }}
              <i class="pi pi-pencil"></i>
            </a>
          </td>
          <td>
            <span
              :style="{
                'text-decoration': service.compromisso_servico_status === 6 ? 'line-through' : 'none'
              }"
            >
              {{ service.servico_nome }}
            </span>
          </td>
          <td>
            <div v-if="service.compromisso_servico_status === 6" class="status-badge status-cancelled">
              <i class="pi pi-times"></i> {{ t('Serviço cancelado') }}
            </div>
            <div v-else class="status-badge status-completed">
              <i class="pi pi-check"></i> {{ t('Finalizado') }}
            </div>
          </td>
          <td>
            <span
              class="service-value"
              :style="{
                'text-decoration': service.compromisso_servico_status === 6 ? 'line-through' : 'none'
              }"
            >
              {{ n(parseFloat(String(service.compromisso_servico_valor ?? 0)) || 0, 'currency') }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Mobile: Card Layout -->
    <div class="services-cards">
      <div
        v-for="service in services"
        :key="service.compromisso_servico_id"
        class="service-card"
      >
        <div class="card-header">
          <div class="card-id">
            <a
              href="javascript:void(0)"
              class="id-link"
              :data-testid="`agenda-review-edit-servico-mobile-${service.compromisso_servico_id}`"
              @click.prevent="handleEditClick(service.compromisso_servico_id ?? 0)"
            >
              <strong>Id:</strong> #{{ service.compromisso_servico_id }}
              <i class="pi pi-pencil"></i>
            </a>
          </div>
          <div v-if="service.compromisso_servico_status === 6" class="status-badge status-cancelled">
            <i class="pi pi-times"></i> {{ t('Serviço cancelado') }}
          </div>
          <div v-else class="status-badge status-completed">
            <i class="pi pi-check"></i> {{ t('Finalizado') }}
          </div>
        </div>

        <div class="card-body">
          <div class="card-field">
            <span class="field-label">{{ t('Serviço') }}:</span>
            <span
              class="field-value"
              :style="{
                'text-decoration': service.compromisso_servico_status === 6 ? 'line-through' : 'none'
              }"
            >
              {{ service.servico_nome }}
            </span>
          </div>

          <div class="card-field">
            <span class="field-label">{{ t('Valor') }}:</span>
            <span
              class="field-value service-value"
              :style="{
                'text-decoration': service.compromisso_servico_status === 6 ? 'line-through' : 'none'
              }"
            >
              {{ n(parseFloat(String(service.compromisso_servico_valor ?? 0)) || 0, 'currency') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface ServicoReview {
  compromisso_servico_id?: Id;
  servico_nome?: string;
  compromisso_servico_status?: number;
  compromisso_servico_valor?: string | number;
  [key: string]: unknown;
}

interface Props {
  services: ServicoReview[];
}

const props = withDefaults(defineProps<Props>(), {
  services: () => []
});

const emit = defineEmits<{
  'edit-service': [servicoId: Id];
}>();

const { t, n } = useI18n();

// Methods
const handleEditClick = (servicoId: Id): void => {
  log('ServicesReviewList', 'handleEditClick', 'Edit button clicked', { servicoId });
  emit('edit-service', servicoId);
};
</script>

<style scoped>
.services-review-list {
  width: 100%;
}

/* Desktop Table Layout */
.services-table {
  width: 100%;
  background-color: white;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.services-table thead th {
  background-color: #f9f9f9;
  font-weight: bold;
  padding: 12px;
  border: 1px solid #ddd;
  text-align: left;
}

.services-table tbody td {
  padding: 12px;
  border: 1px solid #ddd;
  vertical-align: middle;
}

.id-column {
  width: 100px;
  white-space: nowrap;
}

.id-link {
  color: #3b82f6;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
}

.id-link:hover {
  text-decoration: underline;
}

.id-link i {
  font-size: 0.875rem;
}

.service-value {
  font-weight: 600;
  color: #333;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.status-cancelled {
  color: #999;
}

.status-completed {
  color: #00a65a;
}

/* Mobile Card Layout */
.services-cards {
  display: none;
}

/* Responsive */
@media (max-width: 768px) {
  /* Hide table on mobile */
  .services-table {
    display: none;
  }

  /* Show cards on mobile */
  .services-cards {
    display: block;
  }

  .service-card {
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
  }

  .card-id {
    font-size: 0.95rem;
  }

  .card-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .card-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .field-label {
    font-weight: 600;
    font-size: 0.875rem;
    color: #666;
  }

  .field-value {
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .service-card {
    padding: 12px;
  }

  .card-id {
    font-size: 0.875rem;
  }

  .field-label {
    font-size: 0.8rem;
  }

  .field-value {
    font-size: 0.875rem;
  }

  .status-badge {
    font-size: 0.875rem;
  }
}
</style>

